import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button, Modal, RadioButton, RadioButtonGroup, Select, SelectItem, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, TextArea, TextInput,
} from '@carbon/react';
import { Currency, Locked, Unlocked, Logout, Partnership, Password, Tag as TagIcon, TrashCan } from '@carbon/icons-react';
import { formatVnd, fromZoneInput, toZoneInput } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { CURRENCY, Money, When } from '../components/format';
import { useCanDeleteId, XoaIdDialog } from '../components/XoaId';
import { CommandStatus, WAGERING_SOURCE } from '../components/labels';
import { BetsTable } from '../components/BetsTable';
import { PlayerTags, type Player } from './Users';
import type { Command } from './Commands';
import { BonusesPanel, GamesPanel, PasswordDialog, RestrictDialog, RiskPanel, RiskTags, WalletPanel, commandError, useRisk, type PlayFlags } from './Player360';
import { Reveal } from '../components/Reveal';
import { soNguyen } from '../components/soNguyen';
import { LoiTrang } from '../components/LoiTrang';
import { VipPanel } from './Vip';
import { MoneyInput } from '../components/MoneyInput';
import { layFresh, themFresh, type Fresh } from './bankFresh';

type Detail = { user: Player; online: boolean | null };
type BankLink = { id: number; ma_nh: string; ten_nh: string; so_tk: string; ten_tk: string; trang_thai: string; tao_luc: string; xac_minh_luc?: string | null; xac_minh_boi?: string | null };
// security changes that hold withdrawals (backend/lib/rut_rui_ro.js bao_mat_su_kien)
type SecurityEvent = { id: number; loai: string; luc: number; nguon: string | null; chi_tiet: string | null };
const SECURITY_EVENT: Record<string, string> = { mat_khau: 'Đổi mật khẩu đăng nhập', mat_khau_rut: 'Đổi mật khẩu rút', ngan_hang: 'Đổi tài khoản ngân hàng', telegram: 'Liên kết Telegram' };
const SECURITY_SOURCE: Record<string, string> = { nguoi_choi: 'người chơi', cms: 'CMS', telegram: 'bot Telegram' };
type PromoReg = { id: number; ma_km: string; ten_km: string; trang_thai: string; tien_nap: number | null; thuong: number | null; muc_cuoc: number | null; ly_do: string | null; tao_luc: string; doi_luc: string | null };
const PROMO_STATE: Record<string, string> = { cho_nap: 'Chờ nạp', dang_cuoc: 'Đang cược', xong: 'Hoàn thành', huy: 'Đã hủy' };

// Linked bank accounts (withdrawals go only there) and deposit promotion history
function BankPromoPanel({ userId, canUnlink, canVerify, canEdit }: { userId: number; canUnlink: boolean; canVerify: boolean; canEdit: boolean }) {
  const qc = useQueryClient();
  // the list comes from the game DB replica (9-20 s behind he A): what an admin just changed is laid over it
  // until the replica shows it, re-read every 5 s meanwhile (bankFresh.ts)
  const [fresh, setFresh] = useState<Fresh[]>([]);
  const q = useQuery({
    queryKey: ['user-bank', userId],
    queryFn: () => api.get<{ banks: BankLink[]; promos: PromoReg[]; security?: SecurityEvent[]; catalog?: { ma: string; ten: string }[] }>(`/api/admin/users/${userId}/ngan-hang`),
    refetchInterval: fresh.length ? 5000 : false,
  });
  useEffect(() => {
    if (!q.data) return;
    setFresh((f) => (f.length ? layFresh(q.data.banks, f, Date.now()).live : f));
  }, [q.data, q.dataUpdatedAt]);
  const ghiFresh = (id: number, patch: Partial<BankLink>) => setFresh((f) => themFresh(f, id, patch, Date.now()));
  // edit (bankId) or add the first account; withdrawals go to the saved account (he A checks again)
  const [edit, setEdit] = useState<{ bankId?: number; maNh: string; soTk: string; tenTk: string; note: string } | null>(null);
  const e = useMutation({
    mutationFn: (x: NonNullable<typeof edit>) => api.post<{ ok: boolean; khongDoi?: boolean; bank?: BankLink }>(`/api/admin/users/${userId}/ngan-hang/sua`, x),
    onSuccess: (r) => {
      setEdit(null);
      if (r?.bank) ghiFresh(r.bank.id, r.bank);
      qc.invalidateQueries({ queryKey: ['user-bank', userId] });
    },
  });
  const [unlink, setUnlink] = useState<BankLink | null>(null);
  const [verify, setVerify] = useState<BankLink | null>(null);
  const [verifyNote, setVerifyNote] = useState('');
  const m = useMutation({
    mutationFn: (b: BankLink) => api.post(`/api/admin/users/${userId}/ngan-hang/${b.id}/go`),
    onSuccess: (_r, b) => { setUnlink(null); ghiFresh(b.id, { trang_thai: 'go' }); qc.invalidateQueries({ queryKey: ['user-bank', userId] }); },
  });
  // P1-D19: a verified account may receive withdrawals before the player deposited from it
  const v = useMutation({
    mutationFn: (b: BankLink) => api.post(`/api/admin/users/${userId}/ngan-hang/${b.id}/xac-minh`, { verified: !b.xac_minh_luc, note: verifyNote.trim() }),
    onSuccess: (_r, b) => {
      setVerify(null); setVerifyNote('');
      ghiFresh(b.id, { xac_minh_luc: b.xac_minh_luc ? null : new Date().toISOString() });
      qc.invalidateQueries({ queryKey: ['user-bank', userId] });
    },
  });
  if (q.isLoading) return <BlockSkeleton lines={4} />;
  if (q.error || !q.data) return <ErrorState error={q.error ?? new Error('Không đọc được')} onRetry={() => q.refetch()} />;
  const { promos } = q.data;
  const banks = fresh.length ? layFresh(q.data.banks, fresh, Date.now()).banks : q.data.banks;
  const security = q.data.security ?? [];
  const catalog = q.data.catalog ?? [];
  return (
    <div className="stack">
      <h3>Ngân hàng liên kết</h3>
      {canEdit && !banks.some((b) => b.trang_thai === 'dung')
        ? <div><Button kind="tertiary" size="sm" onClick={() => { e.reset(); setEdit({ maNh: '', soTk: '', tenTk: '', note: '' }); }}>Thêm tài khoản</Button></div> : null}
      {banks.length ? (
        <ul className="list">
          {banks.map((b) => (
            <li key={b.id}>
              <strong>{b.ten_nh}</strong> <Reveal masked={b.so_tk} source="bank_link" id={b.id} /> {b.ten_tk}
              <span className="muted"> · liên kết <When v={b.tao_luc} /></span>{' '}
              {b.xac_minh_luc ? <Tag size="sm" type="green">Đã xác minh</Tag> : null}
              {b.trang_thai === 'dung' && canVerify
                ? <Button kind="ghost" size="sm" onClick={() => { v.reset(); setVerifyNote(''); setVerify(b); }}>{b.xac_minh_luc ? 'Bỏ xác minh' : 'Xác minh'}</Button> : null}
              {b.trang_thai === 'dung' && canEdit
                ? <Button kind="ghost" size="sm" onClick={() => { e.reset(); setEdit({ bankId: b.id, maNh: b.ma_nh, soTk: '', tenTk: b.ten_tk.includes('*') ? '' : b.ten_tk, note: '' }); }}>Sửa</Button> : null}
              {b.trang_thai === 'dung' ? (canUnlink ? <Button kind="danger--ghost" size="sm" onClick={() => { m.reset(); setUnlink(b); }}>Gỡ liên kết</Button> : null)
                : <span className="muted">(CSKH đã gỡ)</span>}
            </li>
          ))}
        </ul>
      ) : <EmptyState title="Chưa liên kết ngân hàng" />}
      <h3>Thay đổi bảo mật</h3>
      <p className="muted">Mỗi thay đổi khóa rút một số giờ (trang Rủi ro rút tiền).</p>
      {security.length ? (
        <ul className="list">
          {security.map((e) => (
            <li key={e.id}>
              <strong>{SECURITY_EVENT[e.loai] ?? e.loai}</strong>
              {e.nguon ? <span className="muted"> · {SECURITY_SOURCE[e.nguon] ?? e.nguon}</span> : null}
              <span className="muted"> · <When v={e.luc} /></span>
            </li>
          ))}
        </ul>
      ) : <EmptyState title="Chưa có thay đổi bảo mật" />}
      <h3>Khuyến mãi nạp</h3>
      {promos.length ? (
        <ul className="list">
          {promos.map((p) => (
            <li key={p.id}>
              <strong>{p.ten_km}</strong> {PROMO_STATE[p.trang_thai] ?? p.trang_thai}
              {p.thuong ? <> · nạp <Money v={p.tien_nap ?? 0} />, thưởng <Money v={p.thuong} />, vòng cược <Money v={p.muc_cuoc ?? 0} /></> : null}
              {p.ly_do ? <span className="muted"> ({p.ly_do})</span> : null}
              <span className="muted"> · <When v={p.tao_luc} /></span>
            </li>
          ))}
        </ul>
      ) : <EmptyState title="Chưa đăng ký khuyến mãi nào" />}
      <Modal open={!!verify} modalHeading={verify?.xac_minh_luc ? 'Bỏ xác minh tài khoản này?' : 'Xác minh tài khoản này?'}
        primaryButtonText={verify?.xac_minh_luc ? 'Bỏ xác minh' : 'Xác minh'} secondaryButtonText="Hủy"
        primaryButtonDisabled={v.isPending || verifyNote.trim().length < 3} onRequestClose={() => setVerify(null)} onRequestSubmit={() => verify && v.mutate(verify)}>
        <div className="stack">
          <p>{verify?.xac_minh_luc
            ? 'Người chơi chỉ rút về tài khoản này được khi đã nạp từ đó.'
            : 'Người chơi rút về tài khoản này được dù chưa nạp từ đó. Chỉ xác minh khi đã đối chiếu chủ tài khoản (sao kê, giấy tờ).'}</p>
          {verify ? <p><strong>{verify.ten_nh} {verify.so_tk} {verify.ten_tk}</strong></p> : null}
          <TextInput id="verify-note" labelText="Cách đã xác minh (bắt buộc)" value={verifyNote} onChange={(e) => setVerifyNote(e.target.value)} maxLength={200} />
          <InlineError error={v.error} />
        </div>
      </Modal>
      <Modal open={!!edit} modalHeading={edit?.bankId ? 'Sửa tài khoản liên kết' : 'Thêm tài khoản liên kết'} primaryButtonText="Lưu" secondaryButtonText="Hủy"
        primaryButtonDisabled={e.isPending || !edit?.maNh || (edit?.tenTk.trim().length ?? 0) < 2 || (edit?.note.trim().length ?? 0) < 3 || (!edit?.bankId && !edit?.soTk.trim())}
        onRequestClose={() => setEdit(null)} onRequestSubmit={() => edit && e.mutate(edit)}>
        {edit ? (
          <div className="stack">
            <p>Tiền rút của người chơi sẽ về tài khoản này. Không đổi được khi còn lệnh rút chưa xong. Sau khi lưu, rút tạm khóa theo trang Rủi ro rút tiền và tài khoản cần xác minh lại.</p>
            <Select id="bank-edit-nh" labelText="Ngân hàng" value={edit.maNh} onChange={(x) => setEdit({ ...edit, maNh: x.target.value })}>
              <SelectItem value="" text="Chọn ngân hàng" />
              {catalog.map((c) => <SelectItem key={c.ma} value={c.ma} text={c.ten} />)}
            </Select>
            <TextInput id="bank-edit-so" labelText={edit.bankId ? 'Số tài khoản mới (để trống = giữ số cũ)' : 'Số tài khoản'} value={edit.soTk} inputMode="numeric"
              onChange={(x) => setEdit({ ...edit, soTk: x.target.value })} maxLength={40} />
            <TextInput id="bank-edit-ten" labelText="Tên chủ tài khoản" value={edit.tenTk} onChange={(x) => setEdit({ ...edit, tenTk: x.target.value })} maxLength={80} />
            <TextInput id="bank-edit-note" labelText="Lý do (bắt buộc)" value={edit.note} onChange={(x) => setEdit({ ...edit, note: x.target.value })} maxLength={200} />
            <InlineError error={e.error} />
          </div>
        ) : null}
      </Modal>
      <Modal open={!!unlink} danger modalHeading="Gỡ liên kết ngân hàng này?" primaryButtonText="Gỡ liên kết" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setUnlink(null)} onRequestSubmit={() => unlink && m.mutate(unlink)}>
        <div className="stack">
          <p>Người chơi sẽ phải liên kết lại mới rút tiền và nhận khuyến mãi được. Số tài khoản này vẫn không dùng được cho nick khác.</p>
          {unlink ? <p><strong>{unlink.ten_nh} {unlink.so_tk} {unlink.ten_tk}</strong></p> : null}
          <InlineError error={m.error} />
        </div>
      </Modal>
    </div>
  );
}

type Grant = { id: number; nguon: string; tien: number; he_so: number; them: number; can_sau: number; luc: string;
  go_bot?: number; go_boi?: string | null; go_luc?: string | null; go_ly_do?: string | null; go_duoc?: number };
type Wagering = {
  con_lai: number; nap: number; he_so_nap: number; he_so_nap_khong_km?: number;
  thuong: { con_lai: number; cuoc_du: number; tong_yeu_cau: number };
  lan_cong: Grant[];
};

// Go vong cuoc cua mot lan cong tien nhan vien (owner 2026-10-05): chi phan lan do con no; nap, khuyen mai giu nguyen
function RemoveWageringDialog({ userId, grant, onClose }: { userId: number; grant: Grant | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  useEffect(() => { if (grant) setNote(''); }, [grant]);
  const m = useMutation({
    mutationFn: () => api.post(`/api/admin/users/${userId}/wagering/${grant!.id}/remove`, { note: note.trim() }),
    onSuccess: () => { onClose(); qc.invalidateQueries({ queryKey: ['user-wagering', userId] }); },
  });
  const ready = !!grant && note.trim().length >= 5 && !m.isPending;
  return (
    <Modal open={!!grant} modalHeading="Gỡ vòng cược của lần cộng tiền này?" primaryButtonText="Gỡ vòng cược" secondaryButtonText="Hủy"
      primaryButtonDisabled={!ready} onRequestClose={onClose} onRequestSubmit={() => ready && m.mutate()}>
      {grant ? (
        <div className="stack">
          <p>Lần cộng <Money v={grant.tien} unit /> lúc <When v={grant.luc} /> còn <strong><Money v={grant.go_duoc ?? 0} unit /></strong> vòng cược. Gỡ thì số này không còn phải cược trước khi rút. Tiền đã cộng giữ nguyên; vòng cược tiền nạp, khuyến mãi và các lần thưởng khác giữ nguyên.</p>
          <TextInput id="go-vc-note" labelText="Lý do (bắt buộc)" value={note} maxLength={100} onChange={(e) => setNote(e.target.value)}
            placeholder="Ví dụ: cộng nhầm kèm vòng cược" invalid={note.length > 0 && note.trim().length < 5} invalidText="Ít nhất 5 ký tự" />
          <InlineError error={m.error} />
        </div>
      ) : null}
    </Modal>
  );
}

// Vong cuoc truoc khi rut (P1-D21): so con phai cuoc ma lenh rut kiem, tach nap / tien thuong.
function WageringPanel({ userId }: { userId: number }) {
  const { can } = useAuth();
  const [removing, setRemoving] = useState<Grant | null>(null);
  const q = useQuery({ queryKey: ['user-wagering', userId], queryFn: () => api.get<Wagering>(`/api/admin/users/${userId}/wagering`) });
  if (q.isLoading) return <BlockSkeleton lines={4} />;
  if (q.error || !q.data) return <ErrorState error={q.error ?? new Error('Không đọc được vòng cược')} onRetry={() => q.refetch()} />;
  const w = q.data;
  return (
    <div className="stack">
      <div className="kpis">
        <div className="kpi"><span>Còn phải cược trước khi rút</span><strong><Money v={w.con_lai} unit /></strong></div>
        <div className="kpi"><span>Vòng cược tiền nạp ({w.he_so_nap}x nạp có khuyến mãi, {w.he_so_nap_khong_km ?? w.he_so_nap}x nạp không khuyến mãi)</span><strong><Money v={w.nap} unit /></strong></div>
        <div className="kpi"><span>Vòng cược tiền thưởng</span><strong><Money v={w.thuong.con_lai} unit /></strong></div>
        <div className="kpi"><span>Tổng yêu cầu tiền thưởng đã cộng</span><strong><Money v={w.thuong.tong_yeu_cau} unit /></strong></div>
      </div>
      <p className="muted">Mỗi cược tiền thật trừ cả hai vòng cược, nên số còn phải cược là số lớn hơn. Lệnh rút bị từ chối khi số này lớn hơn 0.</p>
      {w.lan_cong.length ? (
        <table className="cds--data-table cds--data-table--sm">
          <thead><tr><th>Lúc</th><th>Nguồn</th><th>Tiền thưởng</th><th>Hệ số</th><th>Yêu cầu thêm</th><th>Còn lại sau đó</th><th>Gỡ vòng cược</th></tr></thead>
          <tbody>
            {w.lan_cong.map((r) => (
              <tr key={r.id}>
                <td><When v={r.luc} /></td><td>{WAGERING_SOURCE[r.nguon] ?? r.nguon}</td><td><Money v={r.tien} /></td>
                <td className="num">{r.he_so}x</td><td><Money v={r.them} /></td><td><Money v={r.can_sau} /></td>
                <td>{r.go_luc ? <>Đã gỡ <Money v={r.go_bot ?? 0} /> bởi {r.go_boi} lúc <When v={r.go_luc} />{r.go_ly_do ? <span className="muted">: {r.go_ly_do}</span> : null}</>
                  : (r.go_duoc ?? 0) > 0 && can('money.request') ? <Button kind="ghost" size="sm" onClick={() => setRemoving(r)}>Gỡ <Money v={r.go_duoc ?? 0} /></Button> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <EmptyState title="Chưa nhận tiền thưởng có vòng cược" />}
      <RemoveWageringDialog userId={userId} grant={removing} onClose={() => setRemoving(null)} />
    </div>
  );
}

type MoneyRules = { reasons?: Partial<Record<Reason, string>>; maxAdjustVnd?: number };
type Reason = 'den_bu' | 'dieu_chinh' | 'khuyen_mai' | 'thu_hoi';
// the category decides the direction: compensation and promotion add, clawback removes
const REASON_DIR: Record<Reason, 'in' | 'out' | 'both'> = { den_bu: 'in', khuyen_mai: 'in', thu_hoi: 'out', dieu_chinh: 'both' };

// so du hien trong hop tien: mot gia tri la (thieu, null, chu) hien "-", khong lam trang trang
function soDuLabel(v: unknown, cot: 'vin' | 'xu'): string {
  const n = soNguyen(v);
  const s = n === null ? '-' : formatVnd(n);
  return cot === 'vin' ? `Số dư hiện tại ${CURRENCY.symbol}${s}` : `Số dư hiện tại ${s} xu`;
}

function MoneyDialog({ user, open, onClose }: { user: Player; open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const rules = useQuery({ queryKey: ['money-rules'], queryFn: () => api.get<MoneyRules>('/api/admin/money/rules'), enabled: open });
  const [dir, setDir] = useState<'in' | 'out'>('in');
  const [amount, setAmount] = useState('');
  const [cot, setCot] = useState<'vin' | 'xu'>('vin');
  // Tinh / Khong tinh vong cuoc (owner 2026-10-05): every credit picks one, default Khong tinh
  const [wager, setWager] = useState(false);
  const [reason, setReason] = useState<Reason | ''>('');
  const [note, setNote] = useState('');
  // one request key per opening: a double click or a resend of the same request never moves the money twice
  const [key, setKey] = useState(() => crypto.randomUUID());
  useEffect(() => { if (open) { setKey(crypto.randomUUID()); setWager(false); } }, [open]);
  const digits = amount.replace(/^0+/, '');
  const canWager = dir === 'in' && cot === 'vin';
  const allowed = (r: Reason) => REASON_DIR[r] === 'both' || REASON_DIR[r] === dir;
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command }>(`/api/admin/users/${user.id}/money`,
      { delta: (dir === 'out' ? '-' : '') + digits, cot, reason, note: note.trim(), wagering: canWager && wager, key }),
    onSuccess: (r) => { onClose(); nav('/commands/' + r.command.id); },
  });
  const ready = digits.length > 0 && !!reason && allowed(reason) && note.trim().length >= 5 && !m.isPending;
  const name = user.nick_name || user.user_name;
  return (
    <Modal open={open} modalHeading="Cộng hoặc trừ tiền" primaryButtonText="Thực hiện ngay" secondaryButtonText="Hủy"
      primaryButtonDisabled={!ready} onRequestClose={onClose} onRequestSubmit={() => ready && m.mutate()}>
      <div className="stack">
        <p>Lệnh thực hiện ngay, không chờ người duyệt. Mỗi lệnh ghi một dòng sổ giao dịch với loại lý do và ghi chú; bấm lại hay gửi lại không cộng hai lần.</p>
        <RadioButtonGroup legendText="Loại lệnh" name="dir" valueSelected={dir} onChange={(v) => { setDir(v as 'in' | 'out'); if (reason && REASON_DIR[reason] !== 'both' && REASON_DIR[reason] !== v) setReason(''); }}>
          <RadioButton id="dir-in" labelText="Cộng tiền" value="in" />
          <RadioButton id="dir-out" labelText="Trừ tiền" value="out" />
        </RadioButtonGroup>
        <div className="row row--top">
          <MoneyInput id="amt" labelText="Số tiền" value={amount} onChange={setAmount} helperText={soDuLabel(cot === 'vin' ? user.vin : user.xu, cot)} />
          <Select id="cot" labelText="Ví" value={cot} onChange={(e) => setCot(e.target.value as 'vin' | 'xu')}>
            <SelectItem value="vin" text={CURRENCY.code} />
            <SelectItem value="xu" text="xu" />
          </Select>
        </div>
        <Select id="reason" labelText="Loại lý do" value={reason} onChange={(e) => setReason(e.target.value as Reason)}>
          <SelectItem value="" text="Chọn loại lý do" />
          {(Object.keys(REASON_DIR) as Reason[]).filter(allowed).map((r) => <SelectItem key={r} value={r} text={rules.data?.reasons?.[r] ?? r} />)}
        </Select>
        <TextInput id="money-note" labelText="Ghi chú (bắt buộc, ghi vào sổ)" value={note} maxLength={100} onChange={(e) => setNote(e.target.value)}
          helperText="Ví dụ: đền bù sự cố ván 12345, thu hồi cộng nhầm ngày 27/09" invalid={note.length > 0 && note.trim().length < 5} invalidText="Ít nhất 5 ký tự" />
        {canWager ? (
          <RadioButtonGroup legendText="Vòng cược" name="wager" orientation="vertical" valueSelected={wager ? 'tinh' : 'khong'} onChange={(v) => setWager(v === 'tinh')}
            helperText="Tính vòng cược: người chơi phải cược lại số tiền này (theo hệ số của loại tiền) trước khi rút.">
            <RadioButton id="wager-khong" labelText="Không tính vòng cược" value="khong" />
            <RadioButton id="wager-tinh" labelText="Tính vòng cược" value="tinh" />
          </RadioButtonGroup>
        ) : null}
        {digits ? <p><strong>{dir === 'in' ? 'Cộng' : 'Trừ'} {cot === 'xu' ? `${formatVnd(BigInt(digits))} xu` : `${CURRENCY.symbol}${formatVnd(BigInt(digits))}`}</strong> {dir === 'in' ? 'cho' : 'của'} {name} (mã {user.id}){reason ? `, lý do ${rules.data?.reasons?.[reason] ?? reason}` : ''}{canWager ? (wager ? ', tính vòng cược' : ', không tính vòng cược') : ''}.</p> : null}
        <InlineError error={rules.error ?? m.error} />
      </div>
    </Modal>
  );
}

function LockDialog({ user, open, onClose }: { user: Player; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const locking = Number(user.khoa) !== 1;
  const [message, setMessage] = useState('');
  const m = useMutation({
    mutationFn: () => api.post(`/api/admin/users/${user.id}/lock`, { locked: locking, ...(locking && message.trim() ? { message: message.trim() } : {}) }),
    onSuccess: () => { onClose(); setMessage(''); qc.invalidateQueries({ queryKey: ['user', user.id] }); qc.invalidateQueries({ queryKey: ['users'] }); qc.invalidateQueries({ queryKey: ['user-risk', user.id] }); },
  });
  return (
    <Modal open={open} danger={locking} modalHeading={locking ? 'Khóa tài khoản này?' : 'Mở khóa tài khoản này?'}
      primaryButtonText={locking ? 'Khóa tài khoản' : 'Mở khóa'} secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending} onRequestClose={onClose} onRequestSubmit={() => m.mutate()}>
      <div className="stack">
        <p>{locking ? 'Người chơi bị đăng xuất khỏi mọi thiết bị và không đăng nhập lại được cho tới khi mở khóa. Lệnh rút đang chờ bị giữ, không chi được.' : 'Người chơi đăng nhập lại được ngay.'}</p>
        {locking ? <TextArea id="lock-msg" labelText="Lời nhắn người chơi thấy khi đăng nhập (tùy chọn)" rows={2} maxCount={200} enableCounter
          value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ví dụ: Tài khoản đang được kiểm tra, liên hệ CSKH" /> : null}
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

// Test / staff flag: the account keeps playing; the statistics leave it out and rebuild without it.
function TestFlagDialog({ user, open, onClose }: { user: Player; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const flagging = Number(user.la_test) !== 1;
  const m = useMutation({
    mutationFn: () => api.post(`/api/admin/users/${user.id}/test-flag`, { isTest: flagging }),
    onSuccess: () => { onClose(); qc.invalidateQueries({ queryKey: ['user', user.id] }); qc.invalidateQueries({ queryKey: ['users'] }); },
  });
  return (
    <Modal open={open} modalHeading={flagging ? 'Đánh dấu tài khoản thử / nhân viên?' : 'Bỏ đánh dấu tài khoản thử?'}
      primaryButtonText={flagging ? 'Đánh dấu' : 'Bỏ đánh dấu'} secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending} onRequestClose={onClose} onRequestSubmit={() => m.mutate()}>
      <div className="stack">
        <p>{flagging
          ? 'Tài khoản vẫn chơi bình thường, nhưng mọi báo cáo và thống kê bỏ nó ra (cược, thắng, người chơi, nạp đầu, thắng lớn).'
          : 'Tài khoản được tính lại vào mọi báo cáo và thống kê.'}</p>
        <p className="muted">Báo cáo được tính lại từ đầu sổ tiền, mất vài giây đến vài phút.</p>
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

// MKT account (owner 2026-10-07, docs/specs/mkt-tach-quy.md), founder only: the account keeps playing, its stakes and
// wins stay out of every game pool, the reports leave it out and show it apart. One he A command per change.
function MktFlagDialog({ userId, flags, open, onClose }: { userId: number; flags: PlayFlags; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const flagging = !flags.mkt;
  const [last, setLast] = useState<Command | null>(null);
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command; flags: PlayFlags }>(`/api/admin/users/${userId}/flags`, { flag: 'mkt', on: flagging }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['user-risk', userId] });
      qc.invalidateQueries({ queryKey: ['user', userId] });
      if (r.command?.status === 'SUCCEEDED') { setLast(null); onClose(); } else setLast(r.command);
    },
  });
  const err = commandError(last);
  return (
    <Modal open={open} modalHeading={flagging ? 'Đánh dấu tài khoản MKT?' : 'Bỏ đánh dấu tài khoản MKT?'}
      primaryButtonText={flagging ? 'Đánh dấu MKT' : 'Bỏ đánh dấu'} secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending} onRequestClose={() => { setLast(null); onClose(); }} onRequestSubmit={() => m.mutate()}>
      <div className="stack">
        <p>{flagging
          ? 'Tiền cược và tiền thắng của tài khoản này không vào và không lấy ra từ quỹ của mọi game. Tài khoản vẫn chơi, nạp, rút như thường. Báo cáo doanh thu bỏ tài khoản ra và hiện riêng ở Tổng quan điều hành.'
          : 'Tiền cược và tiền thắng của tài khoản được tính lại vào quỹ game, tài khoản vào lại các báo cáo.'}</p>
        <p className="muted">Áp dụng từ ván chốt tiếp theo của mỗi game. Báo cáo được tính lại từ đầu sổ tiền, mất vài giây đến vài phút.</p>
        {err ? <p className="text-error">{err}</p> : null}
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

type Transfer = { id: string; fromUserId: number; toUserId: number; fromName: string; toName: string; amount: string; fee: string; status: string; error: string | null; createdAt: string };

function TransfersTable({ userId }: { userId: number }) {
  const q = useQuery({ queryKey: ['transfers', userId], queryFn: () => api.get<{ items: Transfer[] }>('/api/admin/transfers', { userId, pageSize: 50 }) });
  if (q.isLoading) return <BlockSkeleton lines={4} />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data?.items.length) return <EmptyState title="Chưa có lần chuyển tiền nào" />;
  return (
    <ul className="list">
      {q.data.items.map((t) => (
        <li key={t.id}>
          {t.fromName} (#{t.fromUserId}) → {t.toName} (#{t.toUserId}): <Money v={t.amount} unit />
          {Number(t.fee) ? <>, phí <Money v={t.fee} /></> : null}
          <span className="muted"> <When v={t.createdAt} /></span> <CommandStatus s={t.status === 'DONE' ? 'SUCCEEDED' : t.status === 'FAILED' ? 'FAILED' : 'PENDING'} />
        </li>
      ))}
    </ul>
  );
}

type CoolOff = { until: string | null };
const localInput = (d: Date) => toZoneInput(d); // the time of the CMS time zone

// Cool-off (self-exclusion): no deposit and no bet until the end; login and withdrawals stay open.
// Setting or extending needs users.coolOff; lifting or shortening before the end needs users.coolOffLift.
function CoolOffPanel({ userId }: { userId: number }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['user-cooloff', userId], queryFn: () => api.get<CoolOff>(`/api/admin/users/${userId}/cool-off`) });
  const [dialog, setDialog] = useState<'set' | 'lift' | null>(null);
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: (lift: boolean) => api.post<{ command: Command }>(`/api/admin/users/${userId}/cool-off`, { until: lift ? null : fromZoneInput(until)?.toISOString() ?? null, reason }),
    onSuccess: () => { setDialog(null); setReason(''); qc.invalidateQueries({ queryKey: ['user-cooloff', userId] }); qc.invalidateQueries({ queryKey: ['user', userId] }); },
  });
  if (q.isLoading) return <BlockSkeleton lines={4} />;
  if (q.error || !q.data) return <ErrorState error={q.error ?? new Error('Không đọc được')} onRetry={() => q.refetch()} />;
  const cur = q.data.until;
  const shortens = !!cur && !!until && (fromZoneInput(until)?.getTime() ?? 0) < Date.parse(cur);
  const open = (d: 'set' | 'lift') => {
    m.reset();
    setUntil(localInput(new Date(Math.max(Date.now(), cur ? Date.parse(cur) : 0) + 7 * 86_400_000)));
    setDialog(d);
  };
  return (
    <div className="stack">
      <p>{cur ? <>Đang tự khóa đến <strong><When v={cur} /></strong>. Không nạp tiền và không đặt cược được; đăng nhập và rút tiền vẫn bình thường.</> : 'Không tự khóa.'}</p>
      <div className="row">
        {can('users.coolOff') ? <Button size="sm" kind="tertiary" onClick={() => open('set')}>{cur ? 'Gia hạn tự khóa' : 'Đặt tự khóa'}</Button> : null}
        {cur && can('users.coolOffLift') ? <Button size="sm" kind="danger--ghost" onClick={() => open('lift')}>Gỡ tự khóa trước hạn</Button> : null}
      </div>

      <Modal open={dialog === 'set'} modalHeading={cur ? 'Gia hạn tự khóa' : 'Đặt tự khóa'} primaryButtonText="Lưu" secondaryButtonText="Hủy"
        primaryButtonDisabled={!until || reason.trim().length < 3 || (shortens && !can('users.coolOffLift')) || m.isPending}
        onRequestClose={() => setDialog(null)} onRequestSubmit={() => m.mutate(false)}>
        <div className="stack">
          <p>Người chơi nhận thư trong hộp thư và thông báo trong game với ngày hết hạn. Hết hạn tài khoản tự mở.</p>
          <TextInput id="co-until" type="datetime-local" labelText="Tự khóa đến" value={until} onChange={(e) => setUntil(e.target.value)}
            invalid={shortens && !can('users.coolOffLift')} invalidText="Chỉ chủ sở hữu được rút ngắn tự khóa" />
          <TextArea id="co-reason" labelText="Lý do" rows={2} maxCount={300} enableCounter value={reason} onChange={(e) => setReason(e.target.value)} />
          <InlineError error={m.error} />
        </div>
      </Modal>
      <Modal open={dialog === 'lift'} danger modalHeading="Gỡ tự khóa trước hạn?" primaryButtonText="Gỡ" secondaryButtonText="Hủy"
        primaryButtonDisabled={reason.trim().length < 3 || m.isPending} onRequestClose={() => setDialog(null)} onRequestSubmit={() => m.mutate(true)}>
        <div className="stack">
          <p>Người chơi nạp và cược lại được ngay. Chỉ gỡ khi người chơi đã xác nhận với CSKH.</p>
          <TextArea id="co-lreason" labelText="Lý do" rows={2} maxCount={300} enableCounter value={reason} onChange={(e) => setReason(e.target.value)} />
          <InlineError error={m.error} />
        </div>
      </Modal>
    </div>
  );
}

export function UserDetailPage() {
  const id = Number(useParams().id);
  // ?tab=cuoc mo thang tab Lich su cuoc (link "Xem them" cua cua so duyet rut, owner 2026-10-06)
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { can, me } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['user', id], queryFn: () => api.get<Detail>('/api/admin/users/' + id), enabled: Number.isSafeInteger(id) && id > 0 });
  const [dialog, setDialog] = useState<'money' | 'lock' | 'kick' | 'agent' | 'test' | 'mkt' | 'restrict' | 'password' | null>(null);
  const [xoa, setXoa] = useState(false);
  const canDelete = useCanDeleteId();
  const risk = useRisk(id);
  const [rate, setRate] = useState('80');
  const [agentCode, setAgentCode] = useState('');
  const [parentCode, setParentCode] = useState('');
  const activate = useMutation({
    mutationFn: () => api.post<{ partner: { id: string } }>('/api/admin/agents', {
      gameUserId: id, rateBps: Number(rate), ...(agentCode ? { code: agentCode } : {}), ...(parentCode ? { parentCode } : {}),
    }),
    onSuccess: (r) => { setDialog(null); nav('/agents/' + r.partner.id); },
  });
  const kick = useMutation({
    mutationFn: () => api.post(`/api/admin/users/${id}/kick`),
    onSuccess: () => { setDialog(null); qc.invalidateQueries({ queryKey: ['user', id] }); },
  });

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={8} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error ?? new Error('Mã người chơi không hợp lệ')} onRetry={() => q.refetch()} /></div>;
  const { user: u, online } = q.data;
  const locked = Number(u.khoa) === 1;

  // Dang nhap, Tai khoan lien quan, So giao dich: khong vai tro nao (chu 2026-10-02 "xoa luon o ca admin chinh"); API cung khong con
  const tabs: { label: string; panel: ReactNode; tab?: string }[] = [
    { label: 'Rủi ro và ghi chú', panel: <RiskPanel userId={u.id} /> },
    { label: 'Nạp / rút', panel: <WalletPanel userId={u.id} frozen={locked || !!risk.data?.flags?.withdrawFrozen} /> },
    { label: 'Game và lãi lỗ', panel: <GamesPanel userId={u.id} /> },
    { label: 'Thưởng đã nhận', panel: <BonusesPanel userId={u.id} /> },
    { label: 'Lịch sử cược', tab: 'cuoc', panel: can('bets.view') ? <BetsTable userId={u.id} /> : <EmptyState title="Không có quyền xem lịch sử cược" /> },
    { label: 'Chuyển tiền đại lý', panel: can('agents.view') ? <TransfersTable userId={u.id} /> : <EmptyState title="Không có quyền xem đại lý" /> },
    { label: 'Ngân hàng và khuyến mãi', panel: <BankPromoPanel key={u.id} userId={u.id} canUnlink={can('users.lock')} canVerify={can('wallet.verifyAccount')} canEdit={can('wallet.editAccount')} /> },
    { label: 'Vòng cược', panel: <WageringPanel userId={u.id} /> },
    { label: 'VIP', panel: can('vip.view') ? <VipPanel userId={u.id} /> : <EmptyState title="Không có quyền xem VIP" /> },
    { label: 'Tự khóa', panel: <CoolOffPanel userId={u.id} /> },
  ];
  return (
    <div className="page">
      <PageHeader
        title={u.nick_name || u.user_name || 'Người chơi ' + u.id}
        lead={<>Mã <span className="num">{u.id}</span>, tên đăng nhập {u.user_name} <PlayerTags p={u} online={online} /> <RiskTags marks={risk.data?.marks} />
          {risk.data?.flags?.mkt ? <Tag type="teal" size="sm">MKT</Tag> : null}
          {risk.data?.flags?.withdrawFrozen ? <Tag type="red" size="sm">Dừng rút</Tag> : null}
          {risk.data?.flags?.chatMutedUntil ? <Tag type="warm-gray" size="sm">Cấm chat</Tag> : null}
          {risk.data?.flags?.lockedGames?.length ? <Tag type="cool-gray" size="sm">Khóa {risk.data.flags.lockedGames.length} game</Tag> : null}</>}
        actions={<>
          <Link className="cell-link" to="/users">← Danh sách người chơi</Link>
          {can('money.request') ? <Button kind="primary" renderIcon={Currency} onClick={() => setDialog('money')}>Cộng hoặc trừ tiền</Button> : null}
          {can('users.lock') ? <Button kind={locked ? 'tertiary' : 'danger--tertiary'} renderIcon={locked ? Unlocked : Locked} onClick={() => setDialog('lock')}>{locked ? 'Mở khóa' : 'Khóa tài khoản'}</Button> : null}
          {can('users.lock') && risk.data ? <Button kind="ghost" renderIcon={Locked} onClick={() => setDialog('restrict')}>Hạn chế (rút, chat, game)</Button> : null}
          {can('users.password') ? <Button kind="ghost" renderIcon={Password} onClick={() => setDialog('password')}>Đặt lại mật khẩu</Button> : null}
          {can('users.kick') ? <Button kind="ghost" renderIcon={Logout} onClick={() => setDialog('kick')}>Đăng xuất mọi phiên</Button> : null}
          {can('agents.manage') ? <Button kind="ghost" renderIcon={Partnership} onClick={() => setDialog('agent')}>Kích đại lý</Button> : null}
          {can('users.testFlag') ? <Button kind="ghost" renderIcon={TagIcon} onClick={() => setDialog('test')}>{Number(u.la_test) === 1 ? 'Bỏ đánh dấu tài khoản thử' : 'Đánh dấu tài khoản thử'}</Button> : null}
          {me?.role === 'founder' && risk.data ? <Button kind="ghost" renderIcon={TagIcon} onClick={() => setDialog('mkt')}>{risk.data.flags.mkt ? 'Bỏ đánh dấu tài khoản MKT' : 'Đánh dấu tài khoản MKT'}</Button> : null}
          {canDelete ? <Button kind="danger--ghost" renderIcon={TrashCan} onClick={() => setXoa(true)}>Xóa ID</Button> : null}
        </>}
      />
      <div className="kpis">
        <div className="kpi"><span>Số dư</span><strong><Money v={u.vin} unit /></strong></div>
        <div className="kpi"><span>Tổng đã nạp và cộng</span><strong><Money v={u.vin_total} unit /></strong></div>
        <div className="kpi"><span>Xu</span><strong><Money v={u.xu} /></strong></div>
        <div className="kpi"><span>Điểm VIP</span><strong className="num">{u.vip_point}</strong></div>
      </div>
      <dl className="facts" style={{ marginBottom: 24 }}>
        <dt>Tạo lúc</dt><dd><When v={u.created_at} /></dd>
        <dt>Mã giới thiệu khi đăng ký</dt><dd>{u.ref_code ?? <span className="muted">Không có</span>}</dd>
        <dt>Thống kê</dt><dd>{Number(u.la_test) === 1 ? 'Tài khoản thử / nhân viên, không tính vào báo cáo' : risk.data?.flags?.mkt ? 'Tài khoản MKT: không tính vào quỹ game và báo cáo, xem riêng ở Tổng quan điều hành' : 'Tính vào báo cáo'}</dd>
        <dt>Trực tuyến</dt><dd>{online === null ? <span className="muted">Không hỏi được hệ A</span> : online ? 'Đang online' : 'Không'}</dd>
      </dl>
      <Tabs defaultSelectedIndex={Math.max(0, tabs.findIndex((t) => t.tab && t.tab === params.get('tab')))}>
        <TabList aria-label="Chi tiết người chơi">
          {tabs.map((t) => <Tab key={t.label}>{t.label}</Tab>)}
        </TabList>
        <TabPanels>
          {tabs.map((t) => <TabPanel key={t.label}><LoiTrang khoa={String(u.id)} muc>{t.panel}</LoiTrang></TabPanel>)}
        </TabPanels>
      </Tabs>

      <LoiTrang khoa={String(u.id)} muc><MoneyDialog user={u} open={dialog === 'money'} onClose={() => setDialog(null)} /></LoiTrang>
      <LockDialog user={u} open={dialog === 'lock'} onClose={() => setDialog(null)} />
      <TestFlagDialog user={u} open={dialog === 'test'} onClose={() => setDialog(null)} />
      {risk.data?.flags ? <MktFlagDialog userId={u.id} flags={risk.data.flags} open={dialog === 'mkt'} onClose={() => setDialog(null)} /> : null}
      {risk.data?.flags ? <RestrictDialog userId={u.id} flags={risk.data.flags} open={dialog === 'restrict'} onClose={() => setDialog(null)} /> : null}
      <PasswordDialog userId={u.id} open={dialog === 'password'} onClose={() => setDialog(null)} />
      <XoaIdDialog player={xoa ? { id: u.id, name: u.user_name } : null} onClose={() => setXoa(false)} onDone={() => nav('/users')} />
      <Modal open={dialog === 'kick'} modalHeading="Đăng xuất người chơi khỏi game?" primaryButtonText="Đăng xuất" secondaryButtonText="Hủy"
        primaryButtonDisabled={kick.isPending} onRequestClose={() => setDialog(null)} onRequestSubmit={() => kick.mutate()}>
        <div className="stack">
          <p>Mọi phiên đăng nhập trên mọi thiết bị bị xóa và kết nối game bị ngắt. Người chơi vẫn đăng nhập lại được; muốn chặn hẳn thì khóa tài khoản.</p>
          <InlineError error={kick.error} />
        </div>
      </Modal>
      <Modal open={dialog === 'agent'} modalHeading="Kích đại lý từ người chơi này?" primaryButtonText="Kích" secondaryButtonText="Hủy"
        primaryButtonDisabled={!rate || activate.isPending} onRequestClose={() => setDialog(null)} onRequestSubmit={() => activate.mutate()}>
        <div className="stack">
          <p>Tạo đại lý với tỷ lệ Rolling (bps). Không nhập tuyến trên thì đại lý nằm ngay dưới nhà cái.</p>
          <TextInput id="u-code" labelText="Mã đại lý (tùy chọn)" helperText="3 đến 12 chữ số. Để trống: hệ thống tự cấp." value={agentCode}
            maxLength={12} onChange={(e) => setAgentCode(e.target.value.replace(/\D/g, ''))} />
          <TextInput id="u-parent" labelText="Mã đại lý tuyến trên (tùy chọn)" value={parentCode}
            maxLength={12} onChange={(e) => setParentCode(e.target.value.replace(/\D/g, ''))} />
          <TextInput id="u-rate" labelText="Tỷ lệ (bps)" helperText="Không vượt tỷ lệ của tuyến trên và mức trần nhà cái." value={rate}
            onChange={(e) => setRate(e.target.value.replace(/\D/g, ''))} />
          <InlineError error={activate.error} />
        </div>
      </Modal>
    </div>
  );
}
